import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { IndianRupee, TrendingUp, Users, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RevenueSummary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/revenue-summary`, { headers: { Authorization: `Bearer ${token}` } });
        setData(res.data);
      } catch { toast.error('Failed to load revenue data'); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading || !data) {
    return <div className="flex items-center justify-center h-96"><p className="text-slate-500">Loading revenue data...</p></div>;
  }

  const p = data.periods;

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" data-testid="revenue-title" style={{ fontFamily: 'Manrope, sans-serif' }}>Revenue Summary</h1>
        <p className="text-slate-600">Financial overview across periods, clients, and recruiters</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'This Month', data: p.this_month, color: 'blue' },
          { label: 'Last Month', data: p.last_month, color: 'teal' },
          { label: 'This Quarter', data: p.this_quarter, color: 'cyan' },
          { label: 'This Year', data: p.this_year, color: 'emerald' },
        ].map(({ label, data: d, color }) => (
          <div key={label} className={`bg-gradient-to-br from-${color}-50 to-white border border-${color}-200 rounded p-6`} data-testid={`revenue-card-${label.toLowerCase().replace(' ', '-')}`}>
            <p className={`text-xs font-bold uppercase tracking-wider text-${color}-700 mb-2`}>{label}</p>
            <p className={`text-2xl font-extrabold text-${color}-900`}>
              {d.revenue > 0 ? `₹${d.revenue.toLocaleString('en-IN')}` : '₹0'}
            </p>
            <p className="text-xs text-slate-500 mt-1">{d.closures} closure(s)</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-blue-100 rounded p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Users size={20} className="text-blue-600" />
            <h2 className="text-lg font-bold text-blue-900">Top Recruiters (This Month)</h2>
          </div>
          {data.top_recruiters.length === 0 ? (
            <p className="text-sm text-slate-500">No closures this month yet.</p>
          ) : (
            <div className="space-y-3">
              {data.top_recruiters.map((r, i) => (
                <div key={r.name} data-testid={`top-recruiter-${r.name}`} className="flex items-center justify-between p-3 bg-blue-50 rounded">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">
                      #{i + 1}
                    </span>
                    <div>
                      <p className="text-sm font-bold text-blue-900">{r.name}</p>
                      <p className="text-xs text-slate-500">{r.closures} closure(s)</p>
                    </div>
                  </div>
                  <p className="font-bold text-emerald-700">₹{r.revenue.toLocaleString('en-IN')}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-blue-100 rounded p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Building2 size={20} className="text-blue-600" />
            <h2 className="text-lg font-bold text-blue-900">Client-wise Revenue (This Year)</h2>
          </div>
          {data.client_breakdown.length === 0 ? (
            <p className="text-sm text-slate-500">No revenue data this year.</p>
          ) : (
            <div className="space-y-3">
              {data.client_breakdown.map((c) => {
                const maxRev = data.client_breakdown[0].revenue;
                const pct = maxRev > 0 ? (c.revenue / maxRev) * 100 : 0;
                return (
                  <div key={c.client} data-testid={`client-revenue-${c.client}`}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-semibold text-blue-900">{c.client}</span>
                      <span className="text-sm font-bold text-emerald-700">₹{c.revenue.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded overflow-hidden">
                      <div className="h-full bg-blue-600 rounded transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
